/**
 * CUSTOMER-R4.15-D — normalize historical shipping / NP mode aliases to current model.
 * Does not invent new delivery products; only maps known legacy strings.
 */
import type { CustomerAddressDeliveryType } from "./customer-profile-v2.js";
import type { NovaPoshtaReferenceSelection } from "./nova-poshta-reference-v1.js";
import type { DeliveryMethodId } from "./delivery-profile-v2.js";

export type NormalizedShippingMethod = {
  /** Canonical customer address delivery type, or null if user must re-select. */
  deliveryType: CustomerAddressDeliveryType | null;
  /** UI tabs: branch / parcel_locker / address */
  uiMode: DeliveryMethodId | null;
  /** Persisted NP selection mode */
  selectionMode: NovaPoshtaReferenceSelection["mode"] | null;
  /** true when input was recognized and mapped */
  migrated: boolean;
  /** true when input was non-empty but unknown */
  requiresReselect: boolean;
  raw: string;
};

const CANONICAL: Record<
  string,
  {
    deliveryType: CustomerAddressDeliveryType;
    uiMode: DeliveryMethodId;
    selectionMode: NovaPoshtaReferenceSelection["mode"];
  }
> = {
  NOVA_POSHTA_BRANCH: {
    deliveryType: "NOVA_POSHTA_BRANCH",
    uiMode: "branch",
    selectionMode: "warehouse",
  },
  NOVA_POSHTA_PARCEL_LOCKER: {
    deliveryType: "NOVA_POSHTA_PARCEL_LOCKER",
    uiMode: "parcel_locker",
    selectionMode: "parcel_locker",
  },
  NOVA_POSHTA_COURIER: {
    deliveryType: "NOVA_POSHTA_COURIER",
    uiMode: "address",
    selectionMode: "address",
  },
};

/** Map known historical aliases → canonical triple. */
const ALIASES: Record<string, keyof typeof CANONICAL> = {
  // canonical
  NOVA_POSHTA_BRANCH: "NOVA_POSHTA_BRANCH",
  NOVA_POSHTA_PARCEL_LOCKER: "NOVA_POSHTA_PARCEL_LOCKER",
  NOVA_POSHTA_COURIER: "NOVA_POSHTA_COURIER",
  // selection / UI modes
  warehouse: "NOVA_POSHTA_BRANCH",
  branch: "NOVA_POSHTA_BRANCH",
  parcel_locker: "NOVA_POSHTA_PARCEL_LOCKER",
  postomat: "NOVA_POSHTA_PARCEL_LOCKER",
  address: "NOVA_POSHTA_COURIER",
  courier: "NOVA_POSHTA_COURIER",
  // order / checkout legacy
  nova_poshta: "NOVA_POSHTA_BRANCH",
  nova_poshta_warehouse: "NOVA_POSHTA_BRANCH",
  nova_poshta_branch: "NOVA_POSHTA_BRANCH",
  nova_poshta_postomat: "NOVA_POSHTA_PARCEL_LOCKER",
  nova_poshta_parcel_locker: "NOVA_POSHTA_PARCEL_LOCKER",
  nova_poshta_courier: "NOVA_POSHTA_COURIER",
  nova_poshta_address: "NOVA_POSHTA_COURIER",
};

export function normalizeShippingMethod(raw: string | null | undefined): NormalizedShippingMethod {
  const original = String(raw ?? "").trim();
  if (!original) {
    return {
      deliveryType: null,
      uiMode: null,
      selectionMode: null,
      migrated: false,
      requiresReselect: false,
      raw: original,
    };
  }
  const key = original.replace(/\s+/g, "_");
  const lower = key.toLowerCase();
  const canonKey =
    ALIASES[key] ??
    ALIASES[lower] ??
    ALIASES[key.toUpperCase()] ??
    null;
  if (!canonKey) {
    return {
      deliveryType: null,
      uiMode: null,
      selectionMode: null,
      migrated: false,
      requiresReselect: true,
      raw: original,
    };
  }
  const c = CANONICAL[canonKey]!;
  const alreadyCanonical = original === canonKey || original === c.selectionMode || original === c.uiMode;
  return {
    deliveryType: c.deliveryType,
    uiMode: c.uiMode,
    selectionMode: c.selectionMode,
    migrated: !alreadyCanonical,
    requiresReselect: false,
    raw: original,
  };
}

/** Normalize a persisted NP selection mode in-place shape. */
export function normalizeNpSelectionMode(
  mode: string | null | undefined,
): NovaPoshtaReferenceSelection["mode"] | null {
  return normalizeShippingMethod(mode).selectionMode;
}
